/**
 * Retail Reporting (Sprint 1, Oct 2026) — shared formatting + vocabulary.
 *
 * Money arrives from the API as integer cents and is only formatted here.
 * Every number on the Retail Overview and in the standard reports is
 * computed server-side (`backend/services/retail_reporting`); this file
 * never re-derives a total.
 */
import { normalizeFamily } from "./productFamily";

export const PERIODS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "this_month", label: "This Month" },
  { key: "custom", label: "Custom" },
];

const usd2 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd0 = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const int = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

export const money = (cents) => usd2.format((Number(cents) || 0) / 100);
export const money0 = (cents) => usd0.format(Math.round((Number(cents) || 0) / 100));
export const count = (n) => int.format(Number(n) || 0);

export function moneyShort(cents) {
  const d = (Number(cents) || 0) / 100;
  if (Math.abs(d) >= 1000) {
    const k = d / 1000;
    return `$${(Math.abs(k) >= 10 ? Math.round(k) : Math.round(k * 10) / 10).toString()}k`;
  }
  return `$${Math.round(d)}`;
}

/** Share as a percent label: "37%", or one decimal under 10%. */
export function share(part, whole) {
  const p = whole > 0 ? (part / whole) * 100 : 0;
  if (p > 0 && p < 10) return `${p.toFixed(1)}%`;
  return `${Math.round(p)}%`;
}

/** Change vs the comparison period, or null when there is nothing to compare. */
export function change(current, previous) {
  if (previous === null || previous === undefined || !(previous > 0)) return null;
  return (current - previous) / previous;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parts(iso) {
  const [y, m, d] = String(iso).split("-").map(Number);
  return { y, m, d, dow: new Date(Date.UTC(y, m - 1, d)).getUTCDay() };
}

export function dayLabel(iso, { weekday = false, year = false } = {}) {
  const { y, m, d, dow } = parts(iso);
  return `${weekday ? DAYS[dow] + ", " : ""}${MONTHS[m - 1]} ${d}${year ? ", " + y : ""}`;
}

export function rangeLabel(start, end) {
  if (!start) return "";
  if (start === end) return dayLabel(start, { weekday: true, year: true });
  return `${dayLabel(start)} – ${dayLabel(end, { year: true })}`;
}

export function hourLabel(h) {
  const n = Number(h);
  return `${n % 12 || 12} ${n < 12 ? "AM" : "PM"}`;
}

/* -------------------------------------------------------------------------
 * Product Family colors (approved Oct 2026).
 *
 * Restaurant blue, Grocery amber, Vegetables teal, Meat coral, Unassigned
 * gray. Product Families are tenant-configurable, so any other family gets
 * a deterministic color from a small extra set, chosen by a hash of its
 * normalized name — never by its sales rank or by which families happen to
 * be on screen, so one family keeps one color everywhere.
 * ---------------------------------------------------------------------- */
export const FAMILY_COLORS = {
  restaurant: "#2F5AA8",
  grocery: "#E0952C",
  vegetables: "#0F9488",
  meat: "#C9573C",
};
export const UNASSIGNED_COLOR = "#A39E95";
// Checked with the approved four for color-blind separation (Oct 2026).
const EXTRA_FAMILY_COLORS = ["#6B4FB8", "#A9467A", "#4F7D2A"];

export function familyColor(key) {
  const k = normalizeFamily(key || "");
  if (!k || k === "unassigned") return UNASSIGNED_COLOR;
  if (FAMILY_COLORS[k]) return FAMILY_COLORS[k];
  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return EXTRA_FAMILY_COLORS[h % EXTRA_FAMILY_COLORS.length];
}

/** Server error -> one friendly sentence (never a stack trace). */
export function reportError(err, fallback = "We couldn't load this right now. Please try again.") {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  return fallback;
}
